import { forwardRef, useCallback, useRef, useState } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { ExternalLink, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useGoogleMaps } from '../context/GoogleMapsContext';
import { ACCOMMODATIONS, VENUE } from '../data/accommodations';

// --- Map marker SVG icons (created after Maps API loads) ---
function makePinSvg({ width, height, fill, stroke, strokeWidth, emoji, emojiSize, whiteDisk }) {
  const cx = width / 2;
  const r = cx;
  // Classic teardrop: circle on top, point at bottom
  const path = `M${cx} 0 C${cx * 0.45} 0 0 ${r * 0.45} 0 ${r} C0 ${r * 1.8} ${cx} ${height} ${cx} ${height} S${width} ${r * 1.8} ${width} ${r} C${width} ${r * 0.45} ${cx * 1.55} 0 ${cx} 0 Z`;
  const diskR = r * 0.45;
  const disk = whiteDisk ? `<circle cx="${cx}" cy="${r}" r="${diskR}" fill="white" opacity="0.9"/>` : '';
  const inner = emoji
    ? `<text x="${cx}" y="${r + emojiSize * 0.35}" text-anchor="middle" font-size="${emojiSize}" font-family="Apple Color Emoji, Segoe UI Emoji, sans-serif">${emoji}</text>`
    : '';
  return (
    'data:image/svg+xml;charset=UTF-8,' +
    encodeURIComponent(
      `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">` +
        `<path d="${path}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>` +
        disk +
        inner +
      '</svg>'
    )
  );
}

function makeCircleSvg({ size, fill, borderColor, borderWidth, emoji, emojiSize }) {
  const cx = size / 2;
  const r = cx - borderWidth;
  const inner = emoji
    ? `<text x="${cx}" y="${cx + emojiSize * 0.36}" text-anchor="middle" font-size="${emojiSize}" font-family="Apple Color Emoji, Segoe UI Emoji, sans-serif">${emoji}</text>`
    : '';
  return (
    'data:image/svg+xml;charset=UTF-8,' +
    encodeURIComponent(
      `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">` +
        `<circle cx="${cx}" cy="${cx}" r="${r}" fill="${fill}" stroke="${borderColor}" stroke-width="${borderWidth}"/>` +
        inner +
      '</svg>'
    )
  );
}


function makeIcons(G) {
  const normal  = makePinSvg({ width: 28, height: 38, fill: '#B5985A', stroke: 'white', strokeWidth: 1.5, emoji: null, whiteDisk: true });
  const selected = makePinSvg({ width: 36, height: 48, fill: '#B5985A', stroke: 'white', strokeWidth: 2,   emoji: null, whiteDisk: true });
  const muted    = makePinSvg({ width: 28, height: 38, fill: '#BFBFBF', stroke: 'white', strokeWidth: 1.5, emoji: null, whiteDisk: true });
  const mutedSelected = makePinSvg({ width: 36, height: 48, fill: '#BFBFBF', stroke: 'white', strokeWidth: 2, emoji: null, whiteDisk: true });
  const venue   = makeCircleSvg({ size: 44, fill: 'white', borderColor: '#B5985A', borderWidth: 3, emoji: '🏰', emojiSize: 22 });

  return {
    normal:   { url: normal,   scaledSize: new G.Size(28, 38), anchor: new G.Point(14, 38) },
    selected: { url: selected, scaledSize: new G.Size(36, 48), anchor: new G.Point(18, 48) },
    muted:    { url: muted,    scaledSize: new G.Size(28, 38), anchor: new G.Point(14, 38) },
    mutedSelected: { url: mutedSelected, scaledSize: new G.Size(36, 48), anchor: new G.Point(18, 48) },
    venue:    { url: venue,    scaledSize: new G.Size(44, 44), anchor: new G.Point(22, 22) },
  };
}

const TYPE_KEY = {
  Hotel: 'typeHotel',
  Rental: 'typeRental',
  Airbnb: 'typeAirbnb',
  Gites: 'typeGites',
};

// --- Single accommodation card ---
function getSiteName(url) {
  if (url.includes('airbnb')) return 'Airbnb';
  if (url.includes('abritel')) return 'Abritel';
  if (url.includes('booking.com')) return 'Booking.com';
  if (url.includes('gites-de-france')) return 'Gîtes de France';
  return 'their website';
}

const AccommodationCard = forwardRef(function AccommodationCard(
  { accommodation, number, isSelected, onClick },
  ref
) {
  const { t } = useLanguage();
  const { name, location, type, capacity, distance, price, minNights, noteKey, url, available } = accommodation;
  const isUnavailable = available === false;

  return (
    <div
      ref={ref}
      onClick={onClick}
      className={`
        border rounded-sm p-5 cursor-pointer transition-all duration-200
        ${isSelected
          ? 'border-[#B5985A] shadow-md bg-[#FDFAF5]'
          : 'border-[#E5E5E5] hover:border-[#B5985A] bg-white'
        }
        ${isUnavailable ? 'opacity-50 grayscale' : ''}
      `}
    >
      {/* Top row: number + badges + link */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex items-center justify-center text-xs font-semibold min-w-[1.5rem] h-6 px-1.5 bg-[#2D2A26] text-white rounded-sm whitespace-nowrap">
            #{number}
          </span>
          <span className="inline-block text-xs font-medium px-2 py-0.5 bg-[#F5F3EF] text-[#B5985A] rounded-sm whitespace-nowrap">
            {t(TYPE_KEY[type])}
          </span>
          {isUnavailable && (
            <span className="inline-block text-xs font-medium px-2 py-0.5 bg-[#E5E5E5] text-[#666] rounded-sm whitespace-nowrap">
              {t('fullyBooked')}
            </span>
          )}
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-[#B5985A] text-sm whitespace-nowrap hover:underline"
        >
          {t('viewOn')} {getSiteName(url)}
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      {/* Name — full width */}
      <h3 className="font-serif text-lg text-[#2D2A26] leading-snug">
        {name}
        {location && (
          <span className="text-[#999] font-sans text-sm font-normal ml-2">
            · {location}
          </span>
        )}
      </h3>
      {/* Meta rows — full width */}
      <div className="flex flex-col gap-1 mt-2 text-sm text-[#666]">
        <div className="flex items-center gap-x-3">
          <span className="flex items-center gap-1 whitespace-nowrap">
            <MapPin className="w-3 h-3 text-[#B5985A] shrink-0" />
            {distance === 'Walking distance'
              ? t('walkingDistance')
              : distance.replace(' walk', ` ${t('walk')}`).replace(' drive', ` ${t('drive')}`)}
          </span>
          {capacity && (
            <span className="whitespace-nowrap">
              {capacity
                .replace('guests', t('guests'))
                .replace('bedrooms', t('bedrooms'))}
            </span>
          )}
        </div>
        <div className="flex items-center gap-x-3">
          {price ? (
            <span className="font-medium text-[#2D2A26] whitespace-nowrap">{price.replace('/night', `/${t('perNight')}`)}</span>
          ) : (
            <span className="italic text-[#999]">See website</span>
          )}
          {minNights && (
            <span className="italic text-[#999] whitespace-nowrap">{minNights} {t('nightsMin')}</span>
          )}
        </div>
        {noteKey && (
          <div className="mt-1 italic text-[#B5985A] font-medium">{t(noteKey)}</div>
        )}
      </div>
    </div>
  );
});

// --- Map panel ---
function MapPanel({ selectedId, onPinClick, onMapLoad }) {
  const { t } = useLanguage();
  const { isLoaded, hasKey } = useGoogleMaps();
  const [icons, setIcons] = useState(null);

  const handleMapLoad = useCallback(
    (map) => {
      const G = window.google.maps;
      setIcons(makeIcons(G));
      onMapLoad(map);
    },
    [onMapLoad]
  );

  const mapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    styles: [
      { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    ],
  };

  if (!hasKey) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-[#F5F3EF] gap-3 p-6 text-center">
        <MapPin className="w-8 h-8 text-[#B5985A]" />
        <p className="text-sm text-[#666] max-w-xs">{t('mapUnavailable')}</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-[#F5F3EF]">
        <div className="w-8 h-8 rounded-full border-2 border-[#B5985A] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '100%' }}
      center={VENUE}
      zoom={11}
      onLoad={handleMapLoad}
      options={mapOptions}
    >
      {/* Venue pin */}
      {icons && (
        <Marker
          position={VENUE}
          icon={icons.venue}
          title="Château des Joncherets"
          zIndex={1000}
        />
      )}
      {/* Accommodation pins */}
      {icons &&
        ACCOMMODATIONS.map((acc) => {
          const isUnavailable = acc.available === false;
          const isSelected = selectedId === acc.id;
          const icon = isUnavailable
            ? (isSelected ? icons.mutedSelected : icons.muted)
            : (isSelected ? icons.selected : icons.normal);
          return (
            <Marker
              key={acc.id}
              position={{ lat: acc.lat, lng: acc.lng }}
              icon={icon}
              title={acc.name}
              onClick={() => onPinClick(acc)}
              zIndex={isSelected ? 2000 : (isUnavailable ? 0 : 1)}
            />
          );
        })}
    </GoogleMap>
  );
}

// --- Main page ---
export const AccommodationsPage = () => {
  const { t } = useLanguage();
  const [selectedId, setSelectedId] = useState(null);
  const mapRef = useRef(null);
  const cardRefs = useRef({});

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const handleCardClick = useCallback((acc) => {
    setSelectedId(acc.id);
    if (mapRef.current && window.google?.maps) {
      const coords = new window.google.maps.LatLng(acc.lat, acc.lng);
      const bounds = mapRef.current.getBounds();
      if (!bounds || !bounds.contains(coords)) {
        mapRef.current.panTo(coords);
      }
    }
  }, []);

  const handlePinClick = useCallback((acc) => {
    setSelectedId(acc.id);
    const el = cardRefs.current[acc.id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  return (
    <div className="bg-white">
      {/* ── Full-width header ── */}
      <div className="text-center pt-14 pb-12 px-6">
        <h1 className="font-serif text-4xl sm:text-5xl text-[#2D2A26] mb-4">
          {t('accommodationsTitle')}
        </h1>
        <div className="w-12 h-0.5 bg-[#B5985A] mx-auto mb-5" />
        <p className="text-[#666] max-w-2xl mx-auto">{t('accommodationsDescription')}</p>
        <p className="text-[#999] text-sm mt-2">{t('accommodationsMapNote')}</p>
      </div>

      {/* ── Split panel ── */}
      <div className="flex flex-col lg:flex-row" style={{ minHeight: '100vh' }}>
      {/* ── Map panel (left / top on mobile) ── */}
      <div className="lg:w-1/2 h-[42vh] lg:h-[calc(100vh-4rem)] lg:sticky lg:top-16 shrink-0 lg:pt-4 lg:pr-4 lg:pb-6 lg:pl-6">
        <div className="w-full h-full overflow-hidden lg:rounded-3xl">
          <MapPanel
            selectedId={selectedId}
            onPinClick={handlePinClick}
            onMapLoad={handleMapLoad}
          />
        </div>
      </div>

      {/* ── Card list (right / below on mobile) ── */}
      <div className="lg:w-1/2 overflow-y-auto">
        {/* Cards */}
        <div className="px-6 lg:px-10 pt-4 pb-12 space-y-3">
          {ACCOMMODATIONS.map((acc, idx) => (
            <AccommodationCard
              key={acc.id}
              accommodation={acc}
              number={idx + 1}
              isSelected={selectedId === acc.id}
              onClick={() => handleCardClick(acc)}
              ref={(el) => {
                cardRefs.current[acc.id] = el;
              }}
            />
          ))}
        </div>
      </div>
      </div>
    </div>
  );
};
